import * as apicall from "../components/Apicall";
import { qs } from "./platformService";

// Procurement API client (/api/v1/proc). Buyer-side screens. Permissions, numbering and
// approval workflows are in platformService (module "proc").

/* ---------------- setup (P0) ---------------- */
export const getSettings = () => apicall.get("proc/setup/settings");
export const saveSettings = (data) => apicall.post(data, "proc/setup/settings");
export const getWarehouses = () => apicall.get("proc/setup/warehouses");
export const getRfqTypes = () => apicall.get("proc/setup/rfq-types");
export const initialiseSetup = () => apicall.post({}, "proc/setup/initialise");

// key: document_types
export const listMaster = (key, params = {}) => apicall.get(`proc/setup/masters/${key}${qs(params)}`);
export const saveMaster = (key, id, data) => apicall.post(data, id ? `proc/setup/masters/${key}/${id}` : `proc/setup/masters/${key}`);

/* ---------------- suppliers (P1) ---------------- */
export const listSuppliers = (params = {}) => apicall.get(`proc/suppliers${qs(params)}`);
export const getSupplier = (id) => apicall.get(`proc/suppliers/${id}`);
export const linkableSuppliers = () => apicall.get("proc/suppliers/linkable");
export const listCategories = () => apicall.get("proc/lookups/categories");
// data: { company_name, contact_name, email, phone } for a new supplier, or { core_supplier_id, contact_name, email, phone } for one already in Inventory
export const inviteSupplier = (data) => apicall.post(data, "proc/suppliers/invite");
export const resendInvite = (id) => apicall.post({}, `proc/suppliers/${id}/resend`);
export const revokeInvite = (id) => apicall.post({}, `proc/suppliers/${id}/revoke`);
export const setSupplierStatus = (id, status) => apicall.post({ status }, `proc/suppliers/${id}/status`); // ACTIVE | SUSPENDED
export const setSupplierCategories = (id, categoryIds) => apicall.post({ category_ids: categoryIds }, `proc/suppliers/${id}/categories`);
export const reviewSupplierDocument = (documentId, data) => apicall.post(data, `proc/suppliers/documents/${documentId}/review`); // { status, note }
export const getSupplierDocumentUrl = (documentId) => apicall.get(`proc/suppliers/documents/${documentId}/url`);

/* ---------------- RFQs and quotations (P2) ---------------- */
export const listRfqs = (params = {}) => apicall.get(`proc/rfqs${qs(params)}`); // { q, status: "1,13" }
export const getRfq = (id) => apicall.get(`proc/rfqs/${id}`);
// data: { title, rfq_type, responsible_date, delivery_date, location, instructions, warehouse_id, lines: [...], required_documents: [...], supplier_ids: [...] }
// A part that is left out is left as it was.
export const saveRfq = (id, data) => apicall.post(data, id ? `proc/rfqs/${id}` : "proc/rfqs");
export const publishRfq = (id) => apicall.post({}, `proc/rfqs/${id}/publish`);
export const addRfqSuppliers = (id, supplierIds) => apicall.post({ supplier_ids: supplierIds }, `proc/rfqs/${id}/suppliers`);
export const cancelRfq = (id, reason) => apicall.post({ reason }, `proc/rfqs/${id}/cancel`);
export const extendRfq = (id, date) => apicall.post({ responsible_date: date }, `proc/rfqs/${id}/extend`);
export const markInfoAnswered = (id, supplierId) => apicall.post({}, `proc/rfqs/${id}/suppliers/${supplierId}/info-answered`);
export const getRfqActivity = (id) => apicall.get(`proc/rfqs/${id}/activity`);
export const getEligibleSuppliers = (categoryIds = []) => apicall.get(`proc/rfqs/eligible-suppliers${qs({ category_ids: categoryIds.join(",") })}`);
export const searchStockItems = (q) => apicall.get(`proc/lookups/items${qs({ q })}`);

export const uploadRfqAttachment = (id, file, lineId) => {
  const form = new FormData();
  if (lineId) form.append("line_id", lineId);
  form.append("file", file);
  return apicall.post(form, `proc/rfqs/${id}/attachments`, true);
};
export const removeRfqAttachment = (id, fileId) => apicall.del(`proc/rfqs/${id}/attachments/${fileId}`);
export const getRfqAttachmentUrl = (id, fileId) => apicall.get(`proc/rfqs/${id}/attachments/${fileId}/url`);

export const getRfqQuotations = (id) => apicall.get(`proc/rfqs/${id}/quotations`);
// action: QUALIFY | REJECT (note required) | RESET
export const reviewQuotation = (rfqId, quotationId, action, note) => apicall.post({ action, note }, `proc/rfqs/${rfqId}/quotations/${quotationId}/review`);
export const getQuotationDocumentUrl = (responseId) => apicall.get(`proc/rfqs/quotation-documents/${responseId}/url`);

/* ---------------- messages (P3) ---------------- */
export const listThreads = (params = {}) => apicall.get(`proc/messages/threads${qs(params)}`); // { rfq_id }
export const getThread = (ref) => apicall.get(`proc/messages/thread${qs(ref)}`); // { supplier_id, rfq_id? }
export const getUnreadMessages = () => apicall.get("proc/messages/unread");
// ref: { supplier_id, rfq_id? }. A file makes it a multipart request.
export const sendMessage = (ref, message, file) => {
  if (!file) return apicall.post({ ...ref, message }, "proc/messages");
  const form = new FormData();
  Object.entries(ref).forEach(([k, v]) => v && form.append(k, v));
  if (message) form.append("message", message);
  form.append("file", file);
  return apicall.post(form, "proc/messages", true);
};
export const getMessageFileUrl = (id) => apicall.get(`proc/messages/files/${id}/url`);

/* ---------------- awards and orders (P4) ---------------- */
export const listAwards = (params = {}) => apicall.get(`proc/awards${qs(params)}`); // { status }
export const listRfqAwards = (rfqId) => apicall.get(`proc/rfqs/${rfqId}/awards`);
export const getAward = (id) => apicall.get(`proc/awards/${id}`);
// data: LUMPSUM { quotation_id } | ITEM / SPLIT { allocations: [{ line_id, quotation_id, quantity? }] }. Replaces the RFQ's draft award.
export const saveAward = (rfqId, data) => apicall.post(data, `proc/rfqs/${rfqId}/awards`);
export const submitAward = (id) => apicall.post({}, `proc/awards/${id}/submit`);
export const withdrawAward = (id, comment) => apicall.post({ comment }, `proc/awards/${id}/withdraw`);
export const discardAward = (id) => apicall.del(`proc/awards/${id}`);
export const listOrders = (params = {}) => apicall.get(`proc/orders${qs(params)}`); // { status, q }
export const getOrder = (id) => apicall.get(`proc/orders/${id}`);
export const completeOrder = (id) => apicall.post({}, `proc/orders/${id}/complete`);
export const getOrderInvoiceUrl = (invoiceId) => apicall.get(`proc/orders/invoices/${invoiceId}/url`);

/* ---------------- dashboard (P6) ---------------- */
export const getDashboard = () => apicall.get("proc/dashboard");
